import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Field,
  Spinner,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOnOptionSelectData,
  TagPickerOption,
  TagPickerProps,
} from '@fluentui/react-components';

import { AppState } from '../../../../redux/reducers';
import { ProtocolFormSelection } from '../../../../models/Entities/Protocols/ProtocolVisitForms';
import { GetProtocolVisitForms } from '../../../../services/ProtocolVisitForms.Services';
import { CheckProtocolContractAssociation } from '../../../../services/Contract.Services';
import { ProtocolFormOption, filterOptions, flattenProtocolForms, optionLabel, resolveSelected } from './ProtocolFormSelector.Logic';

export interface ProtocolFormSelectorProps {
  /** Protocolo cuyas plantillas de visita se listan. Si no se conoce, se resuelve a partir de `ContractId`. */
  ProtocolId?: number | undefined;
  /** Contrato: el selector busca el protocolo asociado (EMR) cuando no recibe `ProtocolId`. */
  ContractId?: number | undefined;
  /** Formulario guardado (formId + visita), para mostrarlo elegido al editar. */
  Selected?: Pick<ProtocolFormSelection, 'formId' | 'visitId' | 'formTitle' | 'visitTitle'> | null;
  onSelectedForm: (form: ProtocolFormSelection | undefined) => void;
  label?: string;
  noteText?: string;
  disabled?: boolean;
  required?: boolean;
}

const NO_OPTIONS = 'no-options';

type LoadState = 'idle' | 'resolving' | 'loading' | 'ready' | 'no-protocol' | 'error';

/**
 * Selector de formulario de visita del protocolo (EMR). Lista los formularios de las plantillas de visita
 * vigentes del protocolo asociado al contrato, agrupados como "Visita · Formulario", y devuelve el elegido
 * con su visita. Selección simple. Carga los datos por su cuenta: es un catálogo chico y de sólo lectura.
 */
export const ProtocolFormSelectorComponent = ({ ProtocolId, ContractId, Selected, onSelectedForm, label, noteText, disabled, required }: ProtocolFormSelectorProps) => {
  const { t } = useTranslation();
  const token = useSelector((state: AppState) => state.oidc?.user?.access_token as string | undefined);
  const [resolvedProtocolId, setResolvedProtocolId] = useState<number | undefined>(undefined);
  const [options, setOptions] = useState<ProtocolFormOption[]>([]);
  const [state, setState] = useState<LoadState>('idle');
  const [query, setQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const requestRef = useRef(0);

  // 1) Protocolo: el recibido, o el asociado al contrato en el EMR (protocols/contracts/checkassociation).
  useEffect(() => {
    if (ProtocolId) {
      setResolvedProtocolId(ProtocolId);
      return undefined;
    }
    if (!ContractId || !token) {
      setResolvedProtocolId(undefined);
      setState('no-protocol');
      return undefined;
    }
    const requestId = ++requestRef.current;
    setState('resolving');
    CheckProtocolContractAssociation(ContractId, token)
      .then((protocolId) => {
        if (requestRef.current !== requestId) return;
        setResolvedProtocolId(protocolId ?? undefined);
        if (!protocolId) setState('no-protocol');
      })
      .catch(() => {
        if (requestRef.current !== requestId) return;
        setResolvedProtocolId(undefined);
        setState('error');
      });
    return undefined;
  }, [ProtocolId, ContractId, token]);

  // 2) Formularios de las plantillas de visita del protocolo; una respuesta vieja se descarta.
  useEffect(() => {
    if (!resolvedProtocolId || !token) {
      setOptions([]);
      return undefined;
    }
    const requestId = ++requestRef.current;
    setState('loading');
    GetProtocolVisitForms(resolvedProtocolId, token)
      .then((visits) => {
        if (requestRef.current !== requestId) return;
        setOptions(flattenProtocolForms(visits));
        setState('ready');
      })
      .catch(() => {
        if (requestRef.current !== requestId) return;
        setOptions([]);
        setState('error');
      });
    return undefined;
  }, [resolvedProtocolId, token]);

  const selectedOption = useMemo(() => {
    if (selectedKey) {
      const byKey = options.find((option) => option.key === selectedKey);
      if (byKey) return byKey;
    }
    return resolveSelected(options, Selected);
  }, [options, Selected, selectedKey]);

  // Lo guardado manda cuando cambia desde afuera (p. ej. al abrir otro concepto).
  useEffect(() => {
    setSelectedKey(undefined);
  }, [Selected?.formId, Selected?.visitId, resolvedProtocolId]);

  const visible = useMemo(
    () => filterOptions(options, query, new Set(selectedOption ? [selectedOption.key] : [])),
    [options, query, selectedOption]
  );

  const emit = (option: ProtocolFormOption | undefined) => {
    setSelectedKey(option?.key);
    onSelectedForm(option ? { formId: option.formId, formTitle: option.formTitle, visitId: option.visitId, visitTitle: option.visitTitle } : undefined);
  };

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (_event, data: TagPickerOnOptionSelectData) => {
    if (data.value === NO_OPTIONS) return;
    if (!data.selectedOptions.includes(data.value)) {
      emit(undefined);
      return;
    }
    emit(options.find((option) => option.key === data.value));
    setQuery('');
  };

  const busy = state === 'resolving' || state === 'loading';
  const fieldLabel = label ?? t('protocolFormSelector.label');
  const hint =
    state === 'no-protocol'
      ? t('protocolFormSelector.noProtocol')
      : state === 'error'
        ? t('protocolFormSelector.error')
        : state === 'resolving'
          ? t('protocolFormSelector.resolving')
          : noteText ?? t('protocolFormSelector.hint');
  const emptyText = busy ? t('protocolFormSelector.loading') : t('protocolFormSelector.noResults');

  return (
    <Field
      label={fieldLabel}
      hint={hint}
      required={required}
      validationState={state === 'error' ? 'error' : 'none'}
      data-testid="protocol-form-selector"
      data-state={state}
      data-protocol-id={resolvedProtocolId}
    >
      <div onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}>
        <TagPicker onOptionSelect={onOptionSelect} selectedOptions={selectedOption ? [selectedOption.key] : []} disabled={disabled || !resolvedProtocolId}>
          <TagPickerControl expandIcon={busy ? <Spinner size="extra-tiny" /> : undefined}>
            <TagPickerGroup aria-label={t('protocolFormSelector.selectedGroup')}>
              {selectedOption && (
                <Tag key={selectedOption.key} shape="rounded" value={selectedOption.key} data-testid="protocol-form-selected">
                  {optionLabel(selectedOption)}
                </Tag>
              )}
            </TagPickerGroup>
            <TagPickerInput
              aria-label={fieldLabel}
              placeholder={selectedOption ? '' : t('protocolFormSelector.placeholder')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              data-testid="protocol-form-input"
            />
          </TagPickerControl>
          <TagPickerList>
            {visible.length > 0 ? (
              visible.map((option) => (
                <TagPickerOption value={option.key} key={option.key} text={optionLabel(option)} secondaryContent={option.visitTitle} data-testid={`protocol-form-option-${option.key}`}>
                  {option.formTitle}
                </TagPickerOption>
              ))
            ) : (
              <TagPickerOption value={NO_OPTIONS} text={String(emptyText)}>
                {emptyText}
              </TagPickerOption>
            )}
          </TagPickerList>
        </TagPicker>
      </div>
    </Field>
  );
};

export default ProtocolFormSelectorComponent;
